"use client";

import { useEffect, useState } from "react";
import { useParams } from "next/navigation";
import Link from "next/link";
import { ArrowLeft, ChevronRight } from "lucide-react";
import ProductCard from "@/components/shop/product-card";
import { ProductDisplay } from "@/lib/api/dutchie";
import { useListingScrollRestore } from "@/lib/hooks/use-listing-scroll";

interface SpecialDetails {
  id: string;
  name: string;
  type: string;
  products: ProductDisplay[];
}

export default function SpecialDetailPage() {
  const params = useParams();
  const specialId = params.id as string;
  
  const [special, setSpecial] = useState<SpecialDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  useListingScrollRestore(!loading);

  useEffect(() => {
    async function fetchSpecialDetails() {
      try {
        const res = await fetch(`/api/specials/${specialId}`);
        if (res.ok) {
          const data = await res.json();
          if (data.success) {
            setSpecial(data.special);
          } else {
            setError(data.error || "Failed to load special");
          }
        } else {
          setError("Special not found");
        }
      } catch (err) {
        setError("Failed to load special details");
      } finally {
        setLoading(false);
      }
    }
    
    if (specialId) {
      fetchSpecialDetails();
    }
  }, [specialId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="container mx-auto px-4 py-8">
          <div className="animate-pulse">
            <div className="h-8 w-48 bg-gray-200 rounded mb-4"></div>
            <div className="h-4 w-96 bg-gray-200 rounded mb-8"></div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2 sm:gap-3">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="bg-white rounded-lg p-4">
                  <div className="aspect-square bg-gray-100 rounded mb-3"></div>
                  <div className="h-4 bg-gray-100 rounded mb-2"></div>
                  <div className="h-4 bg-gray-100 rounded w-2/3"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !special) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Special Not Found</h1>
          <p className="text-gray-600 mb-4">{error || "This special may have expired or been removed."}</p>
          <Link 
            href="/specials" 
            className="inline-flex items-center text-primary_green hover:underline"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back to all specials
          </Link>
        </div>
      </div>
    );
  }

  // Check if this is a bundle deal (not a percentage sale)
  const isBundle = special.type === 'OFFER' || special.name.toLowerCase().includes(' for $');

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="container mx-auto px-4 py-4">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-sm text-gray-500 mb-4">
            <Link href="/" className="hover:text-gray-700">Home</Link>
            <ChevronRight className="h-4 w-4" />
            <Link href="/shop" className="hover:text-gray-700">Shop</Link>
            <ChevronRight className="h-4 w-4" />
            <Link href="/specials" className="hover:text-gray-700">Specials</Link>
            <ChevronRight className="h-4 w-4" />
            <span className="text-gray-900 font-medium truncate max-w-[200px]">{special.name}</span>
          </nav>
          
          {/* Title */}
          <div className="flex items-start justify-between gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900">{special.name}</h1>
              <p className="text-gray-600 mt-1">
                {special.products.length} eligible item{special.products.length !== 1 ? 's' : ''}
              </p>
            </div>
            <Link 
              href="/specials"
              className="flex-shrink-0 text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1"
            >
              <ArrowLeft className="h-4 w-4" />
              All Specials
            </Link>
          </div>
          
          {/* Bundle Deal Notice */}
          {isBundle && (
            <div className="mt-4 bg-purple-50 border border-purple-200 rounded-lg px-4 py-3">
              <p className="text-purple-800 text-sm">
                <span className="font-semibold">Bundle Deal:</span> Add eligible items to your cart and the discount will be applied automatically at checkout.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Products Grid */}
      <div className="container mx-auto px-4 py-6">
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2 sm:gap-3">
          {special.products.map((product) => (
            <ProductCard key={`${product.id}-${product.variantId}`} product={product} />
          ))}
        </div>
        
        {special.products.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500">No products currently available for this offer.</p>
          </div>
        )}
      </div>
    </div>
  );
}
